import { useState } from 'react'
import { Search, Bell, User, Menu, X, LogOut, Shield } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  useUnreadNotificationCount,
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../hooks/useNotifications'
import { formatDateTime } from '../utils/formatters'
import toast from 'react-hot-toast'

function Header({ onMenuClick }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const navigate = useNavigate()
  const { user, isAuthenticated, logout, isAdmin } = useAuth()
  const { data: unreadCount = 0 } = useUnreadNotificationCount(isAuthenticated)
  const { data: notificationsData } = useNotificationsList(isAuthenticated && showNotifications)
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  const handleNotificationClick = (n) => {
    if (!n.is_read) markRead.mutate(n.id)
    setShowNotifications(false)
    if (n.link) navigate(n.link)
  }
  
  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchQuery('')
      setShowMobileSearch(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      toast.success('Logged out successfully')
      navigate('/login')
    } else {
      toast.error('Logout failed')
    }
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U'
    const first = user.firstname?.[0] || ''
    const last = user.lastname?.[0] || ''
    if (first || last) return `${first}${last}`.toUpperCase()
    return user.username?.[0]?.toUpperCase() || 'U'
  }
  
  return (
    <header className="h-16 bg-white border-b border-grey-200 flex items-center justify-between px-4 md:px-6">
      {/* Mobile menu button — opens the navigation drawer */}
      <button
        onClick={onMenuClick}
        className="md:hidden p-3 -ml-3 mr-2 hover:bg-grey-100 rounded-lg"
        aria-label="Open menu"
      >
        <Menu className="w-6 h-6 text-grey-700" />
      </button>

      {/* Search Bar - Admin only */}
      {isAdmin() && (
        <>
          <div className={`flex-1 max-w-xl ${showMobileSearch ? 'absolute inset-x-0 top-0 z-50 p-4 bg-white border-b' : 'hidden md:block'}`}>
            {showMobileSearch && (
              <button 
                onClick={() => setShowMobileSearch(false)}
                className="absolute right-4 top-4 p-3 hover:bg-grey-100 rounded-lg"
                aria-label="Close search"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
              <input
                type="text"
                placeholder="Search listings, contacts, agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-grey-100 border-0 rounded-lg focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
                aria-label="Search listings, contacts, agents"
              />
            </form>
          </div>
          
          {/* Mobile Search Toggle */}
          <button
            onClick={() => setShowMobileSearch(true)}
            className="md:hidden p-3 hover:bg-grey-100 rounded-lg"
            aria-label="Open search"
          >
            <Search className="w-5 h-5" />
          </button>
        </>
      )}
      
      {/* Right Section */}
      <div className="flex items-center gap-4 ml-4">
        {/* Notifications */}
        {isAuthenticated && (
          <div className="relative">
            <button
              onClick={() => setShowNotifications((v) => !v)}
              className="relative p-3 hover:bg-grey-100 rounded-lg"
              aria-label="Notifications"
              aria-expanded={showNotifications}
            >
              <Bell className="w-5 h-5 text-grey-600" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 rounded-full text-[10px] font-bold text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white rounded-lg shadow-lg border border-grey-200 z-20">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-grey-100 sticky top-0 bg-white">
                    <p className="text-sm font-semibold text-grey-900">Notifications</p>
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllRead.mutate()}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {!notificationsData || notificationsData.items.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-grey-400">No notifications yet</p>
                  ) : (
                    <div className="divide-y divide-grey-100">
                      {notificationsData.items.map((n) => (
                        <button
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={`w-full text-left px-4 py-2.5 hover:bg-grey-50 ${n.is_read ? '' : 'bg-primary-50/50'}`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.is_read && <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-primary-600 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className={`text-sm ${n.is_read ? 'text-grey-700' : 'text-grey-900 font-medium'}`}>{n.title}</p>
                              {n.body && <p className="text-xs text-grey-500 truncate">{n.body}</p>}
                              <p className="text-[11px] text-grey-400 mt-0.5">{formatDateTime(n.created_at)}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {/* User Menu */}
        {isAuthenticated && user ? (
          <div className="relative">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 pl-4 border-l border-grey-200 hover:bg-grey-50 rounded-lg py-1.5 pr-1.5 transition-colors"
              aria-label="Open user menu"
              aria-expanded={showUserMenu}
            >
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-grey-900">{user.display_name}</p>
                <p className="text-xs text-grey-500 flex items-center justify-end gap-1">
                  {user.is_admin && <Shield className="w-3 h-3 text-blue-500" />}
                  {user.usertype || 'User'}
                </p>
              </div>
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {getUserInitials()}
              </div>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-grey-200 py-1 z-20">
                  <div className="px-4 py-2 border-b border-grey-100 sm:hidden">
                    <p className="text-sm font-medium text-grey-900">{user.display_name}</p>
                    <p className="text-xs text-grey-500">{user.usertype}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-grey-700 hover:bg-grey-100"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin/users"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-grey-700 hover:bg-grey-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Shield className="w-4 h-4" />
                      Admin Panel
                    </Link>
                  )}
                  <hr className="my-1 border-grey-100" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 pl-4 border-l border-grey-200">
            <Link 
              to="/login"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Sign In
            </Link>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
